import { Auth0Provider } from '@auth0/auth0-react'
import { AppState } from '@auth0/auth0-react/src/auth0-provider'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { FC, useMemo } from 'react'
import { RouterProvider } from 'react-router-dom'
import { newQueryClient } from './api/apiUtils'
import { AuthenticationContextProvider } from './auth/AuthenticationContextProvider'
import { ALL_SCOPES, AUTH_PROPERTIES } from './constants'
import { useSnackbar } from './hooks/useSnackbar'
import { appRouter } from './pages/routes' // Import the Blueprint design tokens as CSS variables first
// Import the compass design tokens last
import '@simplisafe/compass-web/index.css'
import './styles/compassOverrides.css'

/**
 * The main application component. It handles user authentication.
 */
export const App: FC = () => {
  const snackbarContext = useSnackbar()

  // we need to make sure that this only gets created once, so putting in a memo
  const queryClient = useMemo(() => newQueryClient(snackbarContext), [snackbarContext])

  return (
    <Auth0Provider
      domain={dex.config.auth0Domain}
      clientId={dex.config.auth0ClientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: ALL_SCOPES.join(' '),
        audience: AUTH_PROPERTIES.MUSEUM_AUDIENCE,
        connection: 'ss-onelogin'
      }}
      cacheLocation={'localstorage'}
      onRedirectCallback={(appState?: AppState): void => {
        appRouter.navigate(appState?.returnTo ?? '/', { replace: true })
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AuthenticationContextProvider>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <RouterProvider router={appRouter} />
          </LocalizationProvider>
        </AuthenticationContextProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Auth0Provider>
  )
}
